import React, { FC, CSSProperties, useEffect, useState, memo } from "react";
import { useFilters, RigItem } from '../contexts/FiltersContext';
import { dropdownStyle } from '../components/StyledSelect';
// Timeline segment images
import firstImg from '../components/first.png';
import secondImg from '../components/second.png';
import thirdImg from '../components/third.png';
import fourthImg from '../components/fourth.png';
import fifthImg from '../components/fifth.png';
import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
  LabelList,
} from "recharts";
import drill from '../components/burenie.gif';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from "../utils/api";

// --- Type Definitions ---

interface TrajectoryDataPoint {
  name: number;
  plan: number | null;
  fact: number | null;
}

interface DrillingPlanDataPoint {
  name: string;
  value: number;
  fill?: string; // per-bar color
}

interface ProgressStep {
  label: string;
  percent: number;
}

interface TopInfoItem {
  label: string;
  value: string;
}

interface GaugeDataItem {
  name: string;
  value: number;
  fill: string;
}

interface VerticalBarProps {
  value: number;
  color: string;
  label: string;
  subLabel: string;
}

interface GaugeChartProps {
  data: GaugeDataItem[];
  type: 'water' | 'pressure';
}

interface HydraulicParam {
    value: number | string;
    label: string;
    baseColor: string;
    fillColor: string;
    fillPercent: number;
    textColor: string;
    fromTop?: boolean;
}

// --- API Types ---

interface StaticRigItem { id: string; name: string; status?: string; well_id?: string; contractor_id?: string; id_int?: string }
interface StaticResponse {
  code: number;
  message: string;
  rig_list: StaticRigItem[];
  org_info: {
    drilling_station: string;
    well: string;
    location: string;
    mine: string;
    contractor: string;
  };
  drilling_plan: Array<{ year: number; month: number; total_meters_drilled: string }>;
  lithology_intervals: Array<any>;
}

interface DynamicResponse {
  code: number;
  message: string;
  header: Array<{ id: string; name: string; status: string }>;
  current_operation: { operation: string; bit_depth: string; well_current_depth: string };
  hydraulic_params: {
    water_flow_rate: string;
    main_pump_pressure: string;
    secondary_pump_pressure: string;
    transmission_pump_pressure: string;
  };
  drilling_params: {
    water_pressure_drilling: string;
    water_pressure_idle: string;
    axial_pressure_hold: string;
    axial_pressure_supply: string;
    water_temperature: string;
    hydraulic_liquid_temperature: string;
  };
  trajectory_points: Array<{
    id: string;
    well_id?: string;
    measurement_depth?: string;
    true_vertical_depth?: string;
    azimuth?: string;
    inclination?: string;
    north_coordinate?: string;
    east_coordinate?: string;
    point_type?: string;
    measured_at?: string;
    depth?: string;
    dtrack?: string;
    u_d?: string;
    l_r?: string;
    northing?: string;
    easting?: string;
    vertical_displacement?: string;
    tlf_offset?: string;
    comment?: string;
    branch_id?: number | string;
    shot_id?: number | string;
  }>;
  drilling_progress: Array<{
    id: string;
    well_id: string;
    operation_type: string;
    stage_name: string;
    start_depth: string;
    end_depth: string;
    started_at: string;
    ended_at: string;
    status: string;
    progress_percent: string;
  }>;
}

// --- Helpers ---

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const toNumber = (s: string | number | undefined | null, def = 0) => {
  if (typeof s === 'number') return s;
  if (typeof s === 'string') {
    const n = Number(String(s).replace(',', '.'));
    return Number.isFinite(n) ? n : def;
  }
  return def;
};
const monthNameRu = (m: number) => {
  const names = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  // API month is 0..11 or 1..12 unknown; normalize
  if (m >= 1 && m <= 12) return names[m-1];
  if (m >= 0 && m <= 11) return names[m];
  return 'Месяц';
};

// Equality helpers to avoid unnecessary re-renders on polling
const shallowEqualArray = <T,>(a: T[], b: T[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const deepEqualArray = (a: any[], b: any[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const av = a[i];
    const bv = b[i];
    if (typeof av === 'object' && typeof bv === 'object') {
      if (JSON.stringify(av) !== JSON.stringify(bv)) return false;
    } else if (av !== bv) return false;
  }
  return true;
};

// --- Widget Components ---

const VerticalBar: FC<VerticalBarProps> = ({ value, color, label, subLabel }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>{label}</div>
        <div style={{ width: 16, height: 220, background: '#2a3f3c', borderRadius: 8, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', bottom: 0, width: '100%', height: `${value}%`, background: color, borderRadius: 8 }}></div>
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{subLabel}</div>
    </div>
);

interface DrillingParametersProps {
  gaugeWater: GaugeDataItem[];
  gaugePressure: GaugeDataItem[];
  waterPressureValue: number | string;
  axialPressureValue: number | string;
  waterTempPercent: number; // 0..100
  hydroTempPercent: number; // 0..100
  waterTempC: number; // raw value for label
  hydroTempC: number; // raw value for label
}

const DrillingParameters: FC<DrillingParametersProps> = ({
  gaugeWater,
  gaugePressure,
  waterPressureValue,
  axialPressureValue,
  waterTempPercent,
  hydroTempPercent,
  waterTempC,
  hydroTempC,
}) => {
  return (
    <div style={{...styles.widget, flex: 1 }}>
      <h2 style={styles.widgetTitle}>Буровые параметры</h2>
    <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center", flex: 1, gap: 12 }}>
        
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 200, position: 'relative' }}>
            <GaugeChart data={gaugeWater} type="water" />
            <div style={{ textAlign: 'center', position: 'absolute', left: 80 }}>
          <div style={{ fontSize: 60, fontWeight: 800, color: '#82FF9E', lineHeight: 1 }}>{waterPressureValue}</div>
                <div style={{ fontSize: 12}}>Давление воды<br/>(Бар)</div>
            </div>
            <div style={{position: 'absolute', top: 4, left: 158, color: '#aaa', fontSize: 12, textAlign: 'left'}}>
                <div>Холостое</div>
                <div>Буровое</div>
            </div>
            <div style={{position: 'absolute', bottom: 4, left: 158, color: '#aaa', fontSize: 12, textAlign: 'left'}}>
                <div>Буровое</div>
                <div>Холостое</div>
            </div>
        </div>

    <div style={{display: 'flex', flexDirection: 'column', gap: 10, alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center', paddingLeft: 24, paddingRight: 24}}>
      <div style={{ color: '#aaa', fontSize: 12 }}>Температура</div>
      <div style={{ display: 'flex', gap: 16 }}>
        <VerticalBar value={waterTempPercent} color="linear-gradient(to top, #3F74EE, #82FF9E)" label="" subLabel={`Вода ${Math.round(waterTempC)} °C`} />
        <VerticalBar value={hydroTempPercent} color="linear-gradient(to top, #F13D3D, #FF9C38)" label="" subLabel={`Гидро ${Math.round(hydroTempC)} °C`} />
      </div>
    </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 200, height: 300, position: 'relative' }}>
      <div style={{ textAlign: 'center', position: 'absolute', right: 80}}>
        <div style={{ fontSize: 60, fontWeight: 800, color: '#FF9B38', lineHeight: 1 }}>{axialPressureValue}</div>
                <div style={{ fontSize: 12 }}>Осевое давление<br/>(Бар)</div>
            </div>
            <GaugeChart data={gaugePressure} type="pressure" />
            <div style={{position: 'absolute', top: 5, right: 150, color: '#aaa', fontSize: 12, textAlign: 'right'}}>
                <div>Подачи</div>
                <div>Удержания</div>
            </div>
            <div style={{position: 'absolute', bottom: 5, right: 150, color: '#aaa', fontSize: 12, textAlign: 'right'}}>
                <div>Удержания</div>
                <div>Подачи</div>
            </div>
        </div>

      </div>
    </div>
  );
}

const GaugeChart: FC<GaugeChartProps> = ({ data, type }) => {
    const isLeft = type === 'water';
    return (
      <div style={{ textAlign: "center", position: 'relative', width: 220, height: 300, pointerEvents: 'none' }}>
        <ResponsiveContainer width="300%" height="100%">
          <RadialBarChart
            innerRadius="75%"
            outerRadius="105%"
            data={data}
            startAngle={isLeft ? 270 : -90}
            endAngle={isLeft ? 90 : 90}
            barSize={12}
            cy="50%"
            cx={isLeft ? "25%" : "10%"}
          >
            {/* Fix domain so 0..100 maps to empty..full arc */}
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <defs>
                <linearGradient id="waterGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#82FF9E" />
                    <stop offset="100%" stopColor="#3F74EE" />
                </linearGradient>
                <linearGradient id="waterGradient2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#82FF9E" />
                    <stop offset="100%" stopColor="#3F74EE" />
                </linearGradient>
                <linearGradient id="pressureGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF9C38" />
                    <stop offset="100%" stopColor="#F13D3D" />
                </linearGradient>
                <linearGradient id="pressureGradient2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF9C38" />
                    <stop offset="100%" stopColor="#F13D3D" />
                </linearGradient>
            </defs>
            <RadialBar
              background={{ fill: '#2a3f3c' }}
              dataKey="value"
              cornerRadius={6}
              isAnimationActive={false}
            />
             <Tooltip contentStyle={{ background: "#333", border: "none" }}/>
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
    );
}


interface TopInfoBarProps { items: TopInfoItem[]; rigs: RigItem[]; selectedStationId?: string; onStationChange: (id: string) => void }

const TopInfoBar: FC<TopInfoBarProps> = ({ items, rigs, selectedStationId, onStationChange }) => {
    return (
        <div style={styles.topInfoBar}>
          {items.map(item => (
            <div key={item.label} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#aaa' }}>{item.label}</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{item.value}</div>
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, marginLeft: 'auto' }}>
            <select 
              style={{ ...dropdownStyle, width: 120, height: 28 }} 
              value={selectedStationId || ''} 
              onChange={e => onStationChange(e.target.value)}
            >
              {rigs.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
    );
}


interface CurrentProcessProps { operation: string; bitDepth: string; wellDepth: string; stationName?: string; stationStatus?: string }
const CurrentProcess: FC<CurrentProcessProps> = ({ operation, bitDepth, wellDepth, stationName, stationStatus }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      
      <div style={{ fontSize: 14, color: '#aaa' }}>Текущий процесс:</div>
      <div style={{ color: "#00ff6a", fontWeight: 800, fontSize: 32, margin: '8px 0', letterSpacing: 0.2, textAlign: 'center' }}>{stationStatus}</div>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <img
          src={drill}
          alt="bit"
          style={{ width: '100%', height: 'auto', margin: '12px 0', objectFit: 'contain' }}
        />
      </div>
      <div style={{ fontSize: 14 }}>
        Долото: <span style={{ fontWeight: 600 }}>{bitDepth}</span>
      </div>
      <div style={{ fontSize: 14 }}>
        Забой: <span style={{ fontWeight: 600 }}>{wellDepth}</span>
      </div>
    </div>
  );
}

interface HydraulicParametersProps { params: HydraulicParam[] }
const HydraulicParameters: FC<HydraulicParametersProps> = ({ params }) => {
  return (
  <div>
      <h3 style={styles.widgetTitle}>Гидравлические параметры</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(140px, 1fr))', alignItems: 'center', justifyItems: 'center' }}>
        {params.map((p, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{
              width: 140,
              height: 140,
              borderRadius: '50%',
              background: p.baseColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 40,
              fontWeight: '800',
              color: p.textColor,
              marginBottom: 14,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 0 0 2px rgba(0,0,0,0.4)'
                        }}>
                            <div style={{
                                position: 'absolute',
                                ...(p.fromTop ? { top: 0 } : { bottom: 0 }),
                                left: 0,
                                right: 0,
                                height: `${p.fillPercent}%`,
                                background: p.fillColor,
                transition: 'height .4s ease'
              }} />
              <span style={{ zIndex: 1, position: 'relative', textShadow: '0 2px 6px rgba(0,0,0,0.45)' }}>{p.value}</span>
                        </div>
            <div style={{ fontSize: 13, width: 140, lineHeight: 1.25 }}>{p.label}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface DrillingPlanProps { data: DrillingPlanDataPoint[]; delta: number }
const DrillingPlan: FC<DrillingPlanProps> = ({ data, delta }) => {
  const maxVal = data.length ? Math.max(...data.map(d => d.value)) : 0; // kept if needed later
  return (
    <div style={styles.widget}>
      <h3 style={styles.widgetTitle}>
        План по бурению {" "}
        <span style={{ color: delta >= 0 ? '#00ff6a' : '#ff4d4f', fontWeight: 700 }}>
          {delta >= 0 ? `+${delta}м` : `${delta}м`}
        </span>
      </h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 12, right: 10, left: 6, bottom: 8 }} barCategoryGap="28%">
          <defs>
            <linearGradient id="barOrange" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFE0B0"/>
              <stop offset="35%" stopColor="#FFC46F"/>
              <stop offset="100%" stopColor="#FF8A00"/>
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#2E3749" strokeDasharray="4 4" vertical={false} />
          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#D4DAE3', fontSize: 12, fontWeight: 600 }} />
          <YAxis hide domain={[0, 'dataMax + 50']} />
          <Bar dataKey="value" radius={[8, 8, 0, 0]} isAnimationActive={false} fill="url(#barOrange)">
            <LabelList dataKey="value" position="top" style={{ fontSize: 12, fontWeight: 700, fill: '#FFFFFF' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface TrajectoryChartProps { data: TrajectoryDataPoint[] }
const TrajectoryChart: FC<TrajectoryChartProps> = ({ data }) => {
  return (
    <div style={styles.widget}>
      <h3 style={styles.widgetTitle}>Траектория Верх/Вниз</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis dataKey="name" tick={{ fill: "#aaa", fontSize: 12 }} />
          <YAxis tick={{ fill: "#aaa", fontSize: 12 }} />
          <Tooltip contentStyle={{ background: "#333", border: "none" }} />
          <Legend wrapperStyle={{ color: '#ccc', fontSize: 12 }} />
          <Line type="monotone" dataKey="plan" stroke="#ffb347" strokeWidth={2} dot={false} name="План" isAnimationActive={false} connectNulls />
          <Line type="monotone" dataKey="fact" stroke="#00aaff" strokeWidth={2} dot={false} name="Факт" isAnimationActive={false} connectNulls />
          {/* Disable animation for lines */}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const SimpleTimeline: FC = () => {
  // Width proportions roughly reflecting earlier colored layout (25,15,20,20,20 = 100)
  const segments = React.useMemo(() => [
    { img: firstImg, alt: 'Этап 1', w: 25 },
    { img: secondImg, alt: 'Этап 2', w: 15 },
    { img: thirdImg, alt: 'Этап 3', w: 20 },
    { img: fourthImg, alt: 'Этап 4', w: 20 },
    { img: fifthImg, alt: 'Этап 5', w: 20 },
  ], []);
  const EDGE_CROP_PCT = 5; // crop 5% from each side visually

  // We crop the original image content (left/right) so internal separator lines at edges are removed.
  // This creates new data URLs once on mount.
  const [cropped, setCropped] = useState<typeof segments>(segments);

  useEffect(() => {
    let cancelled = false;
    const promises = segments.map(seg => new Promise<{ img: string; alt: string; w: number }>(resolve => {
      const image = new Image();
      image.src = seg.img;
      image.onload = () => {
        try {
          const cropLeft = Math.round(image.width * (EDGE_CROP_PCT / 100));
          const cropRight = Math.round(image.width * (EDGE_CROP_PCT / 100));
          const cropWidth = Math.max(1, image.width - cropLeft - cropRight);
          const canvas = document.createElement('canvas');
          canvas.width = cropWidth;
          canvas.height = image.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(image, cropLeft, 0, cropWidth, image.height, 0, 0, cropWidth, image.height);
            const dataUrl = canvas.toDataURL();
            resolve({ img: dataUrl, alt: seg.alt, w: seg.w });
          } else {
            resolve(seg); // fallback
          }
        } catch {
          resolve(seg); // fallback on any error
        }
      };
      image.onerror = () => resolve(seg);
    }));
    Promise.all(promises).then(list => {
      if (!cancelled) setCropped(list);
    });
    return () => { cancelled = true; };
  }, [segments]);

  return (
    <div style={{ ...styles.widget, minHeight: 'auto', padding: '16px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#aaa', fontSize: 12, marginBottom: 4 }}>
        <span>0</span>
        <span>50</span>
        <span>100</span>
        <span>150</span>
        <span>200</span>
        <span>250</span>
        <span>300</span>
      </div>
      <div style={{ display: 'flex', height: 40, background: '#111', borderRadius: 6, overflow: 'hidden' }}>
        {cropped.map((s, i) => (
          <div key={i} style={{ flexGrow: s.w, flexBasis: 0, position: 'relative', overflow: 'hidden' }} aria-label={s.alt}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${s.img})`,
                backgroundRepeat: 'repeat-x',
                backgroundSize: 'auto 100%',
                backgroundPosition: 'center',
                imageRendering: 'crisp-edges'
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

interface ProgressBarProps { steps: ProgressStep[] }
const ProgressBar: FC<ProgressBarProps> = ({ steps }) => {
  const clamped = (p: number) => Math.round(clamp01(p / 100) * 100);
  const lastPercent = steps.length ? Math.max(...steps.map(s => clamped(s.percent))) : 0;
  const sortedSteps = [...steps].sort((a, b) => a.percent - b.percent);

  return (
    <div style={{...styles.widget, padding: '16px 24px', minHeight: 'auto', overflow: 'hidden' }}>
      {/* Labels above markers */}
      <div style={{ position: 'relative', height: 40, marginBottom: 8, overflow: 'hidden' }}>
        {sortedSteps.map((step, i) => {
          const p = clamped(step.percent);
          const align = p <= 2 ? 'left' : p >= 98 ? 'right' : 'center';
          const left = align === 'left' ? '0%' : align === 'right' ? '100%' : `${p}%`;
          const translateX = align === 'left' ? '0' : align === 'right' ? '-100%' : '-50%';
          return (
            <div key={i} style={{
              position: 'absolute',
              left,
              transform: `translateX(${translateX})`,
              textAlign: 'center',
              color: '#aaa',
              fontSize: 12,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              <div>{step.label}</div>
              <div style={{color: '#fff', fontWeight: 'bold'}}>{p}%</div>
            </div>
          );
        })}
      </div>

      {/* Full-width track + points at exact positions */}
      <div style={{ position: 'relative', width: '100%', height: 8, background: '#111', borderRadius: 4 }}>
        <div style={{ position: 'absolute', height: '100%', width: `${lastPercent}%`, background: 'linear-gradient(90deg, #00ff6a, #00aaff)' , borderRadius: 4 }} />
        {sortedSteps.map((step, i) => {
          const p = clamped(step.percent);
          return (
            <div key={i} style={{
              position: 'absolute',
              left: `${p}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#18191c',
              border: '3px solid #00ff6a',
              zIndex: 1,
            }} />
          );
        })}
      </div>
    </div>
  );
}


// --- Main Page Component ---

const HomePage: FC = () => {
  const { user } = useAuth();
  const { rigs, selectedStationId, setSelectedStationId } = useFilters();

  // State
  const [topInfo, setTopInfo] = useState<TopInfoItem[]>([]);
  const [planData, setPlanData] = useState<DrillingPlanDataPoint[]>([]);
  const [planDelta, setPlanDelta] = useState<number>(0);
  const [trajData, setTrajData] = useState<TrajectoryDataPoint[]>([]);
  const [gaugeWater, setGaugeWater] = useState<GaugeDataItem[]>([
    { name: 'Холостое', value: 0, fill: 'url(#waterGradient)' },
    { name: 'Буровое', value: 0, fill: 'url(#waterGradient2)' },
  ]);
  const [gaugePressure, setGaugePressure] = useState<GaugeDataItem[]>([
    { name: 'Подачи', value: 0, fill: 'url(#pressureGradient)' },
    { name: 'Удержания', value: 0, fill: 'url(#pressureGradient2)' },
  ]);
  const [waterPressureValue, setWaterPressureValue] = useState<number | string>(0);
  const [axialPressureValue, setAxialPressureValue] = useState<number | string>(0);
  const [waterTempPercent, setWaterTempPercent] = useState<number>(0);
  const [hydroTempPercent, setHydroTempPercent] = useState<number>(0);
  const [waterTempC, setWaterTempC] = useState<number>(0);
  const [hydroTempC, setHydroTempC] = useState<number>(0);
  const [hydraulicParams, setHydraulicParams] = useState<HydraulicParam[]>([]);
  const [currentOp, setCurrentOp] = useState<{ operation: string; bitDepth: string; wellDepth: string }>({ operation: '—', bitDepth: '—', wellDepth: '—' });
  const [headerInfo, setHeaderInfo] = useState<{ name: string; status: string }>({ name: '—', status: '—' });
  const [progress, setProgress] = useState<ProgressStep[]>([]);

  const token = user?.access_token;

  // Station list and selection are now global via FiltersContext (Header)

  const fetchStatic = async (stationId?: string) => {
    try {
      const params = new URLSearchParams();
      if (stationId) params.set('station_id', stationId);
      const url = `${apiUrl('/summary/static')}${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) throw new Error(`Static fetch failed: ${res.status}`);
      const data: StaticResponse = await res.json();
      // Update top info using org_info provided by static endpoint (handle both legacy and new keys)
      const org = (data as any).org_info || {};
      const newTopInfo: TopInfoItem[] = [
        { label: 'Компания', value: org.contractor_name || org.contractor || '—' },
        { label: 'Месторождение', value: org.deposit_name || org.location || '—' },
        { label: 'Шахта', value: org.mine_name || org.mine || '—' },
        { label: 'Скважина', value: org.well_name || org.well || '—' },
        { label: 'Станок', value: org.station_name || org.drilling_station || '—' },
      ];
      if (!deepEqualArray(topInfo, newTopInfo)) setTopInfo(newTopInfo);
      const rawPlan = (data.drilling_plan || []).map(dp => ({
        name: `${monthNameRu(dp.month)}`,
        value: Math.round(toNumber(dp.total_meters_drilled, 0)), // ensure integer
      }));
      const newPlanData = rawPlan.map(p => ({ ...p }));
      // delta: difference between last and previous month values
      if (rawPlan.length >= 2) {
        const last = Math.round(rawPlan[rawPlan.length - 1].value);
        const prev = Math.round(rawPlan[rawPlan.length - 2].value);
        const newDelta = Math.round(last - prev); // integer delta
        if (planDelta !== newDelta) setPlanDelta(newDelta);
      }
      if (!deepEqualArray(planData, newPlanData)) setPlanData(newPlanData);
    } catch (e) {
      console.error(e);
    }
  };

  // Org info is now set from fetchStatic(org_info); no separate resolver needed

  const fetchDynamic = async (stationId?: string) => {
    try {
      const params = new URLSearchParams();
      if (stationId) params.set('station_id', stationId);
      const url = `${apiUrl('/summary/dynamic')}${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) throw new Error(`Dynamic fetch failed: ${res.status}`);
      const data: DynamicResponse = await res.json();

      // Drilling/current operation
      const op = data.current_operation || { operation: '', bit_depth: '', well_current_depth: '' };
      const translateOp = (s?: string) => {
        const v = String(s || '').trim().toLowerCase();
        if (!v) return '—';
        // Match common keywords and prefixes in English/Russian
        const tests: Array<{ keys: string[]; label: string }> = [
          { keys: ['drill', 'бурен'], label: 'Бурение' },
          { keys: ['casing', 'completed', 'заверш', 'крепл'], label: 'Завершена' },
          { keys: ['maint', 'service', 'repair', 'обслуж', 'ремонт'], label: 'Обслуживание' },
          { keys: ['stop', 'paused', 'останов', 'простой'], label: 'Остановлен' },
          { keys: ['offline', 'no signal', 'нет сигнала', 'оффлайн'], label: 'Не в сети' },
        ];
        for (const t of tests) {
          if (t.keys.some(k => v.includes(k))) return t.label;
        }
        return s || '—';
      };
      const opNameRu = translateOp(op.operation);
      // Header station name/status (match selected station)
      const headerList = Array.isArray(data.header) ? data.header : [];
      const hItem = headerList.find(h => h.id === selectedStationId) || headerList[0];
      if (hItem) {
        const statusRu = translateOp(hItem.status);
        const newHeader = { name: hItem.name || '—', status: statusRu };
        if (JSON.stringify(headerInfo) !== JSON.stringify(newHeader)) setHeaderInfo(newHeader);
      }
      const formatDepth = (s?: string | number) => {
        if (s === undefined || s === null || s === '') return '—';
        const n = toNumber(s, NaN);
        if (!Number.isFinite(n)) return String(s);
        return `${Math.round(n)} м`;
      };
      const bitRaw = op.bit_depth ?? (op as any).current_bit_depth ?? (op as any).bitdepth;
      const wellRaw = op.well_current_depth ?? (op as any).well_depth ?? (op as any).welldepth;
      const newCurrentOp = {
        operation: opNameRu,
        bitDepth: formatDepth(bitRaw),
        wellDepth: formatDepth(wellRaw),
      };
      if (JSON.stringify(currentOp) !== JSON.stringify(newCurrentOp)) setCurrentOp(newCurrentOp);

      // Gauges
      const dp = data.drilling_params || ({} as DynamicResponse['drilling_params']);
      const waterIdle = toNumber(dp.water_pressure_idle, 0);
      const waterDrill = toNumber(dp.water_pressure_drilling, 0);
      const waterIdlePct = Math.round(clamp01(waterIdle / 50) * 100);
      const waterDrillPct = Math.round(clamp01(waterDrill / 50) * 100);
      // Swap arc positions: place 'Буровое' first, then 'Холостое'
      const newGaugeWater: GaugeDataItem[] = [
        { name: 'Буровое', value: waterDrillPct, fill: 'url(#waterGradient2)' },
        { name: 'Холостое', value: waterIdlePct, fill: 'url(#waterGradient)' },
      ];
      if (!deepEqualArray(gaugeWater, newGaugeWater)) setGaugeWater(newGaugeWater);
      const newWaterPressureValue = Math.round(waterDrill);
      if (waterPressureValue !== newWaterPressureValue) setWaterPressureValue(newWaterPressureValue);

      const axialHold = toNumber(dp.axial_pressure_hold, 0);
      const axialSupply = toNumber(dp.axial_pressure_supply, 0);
      const axialSupplyPct = Math.round(clamp01(axialSupply / 50) * 100);
      const axialHoldPct = Math.round(clamp01(axialHold / 50) * 100);
      // Swap arc positions: place 'Удержания' first, then 'Подачи'
      const newGaugePressure: GaugeDataItem[] = [
        { name: 'Удержания', value: axialHoldPct, fill: 'url(#pressureGradient2)' },
        { name: 'Подачи', value: axialSupplyPct, fill: 'url(#pressureGradient)' },
      ];
      if (!deepEqualArray(gaugePressure, newGaugePressure)) setGaugePressure(newGaugePressure);
      const newAxialPressureValue = Math.round(axialHold);
      if (axialPressureValue !== newAxialPressureValue) setAxialPressureValue(newAxialPressureValue);

      // Temps -> vertical bars percent (0..100)
      const waterT = toNumber(dp.water_temperature, 0);
      const hydroT = toNumber(dp.hydraulic_liquid_temperature, 0);
      const newWaterTempPercent = Math.round(clamp01(waterT / 100) * 100);
      const newHydroTempPercent = Math.round(clamp01(hydroT / 100) * 100);
      if (waterTempPercent !== newWaterTempPercent) setWaterTempPercent(newWaterTempPercent);
      if (hydroTempPercent !== newHydroTempPercent) setHydroTempPercent(newHydroTempPercent);
      if (waterTempC !== waterT) setWaterTempC(waterT);
      if (hydroTempC !== hydroT) setHydroTempC(hydroT);

      // Hydraulic params widget
      const hp = data.hydraulic_params || ({} as DynamicResponse['hydraulic_params']);
      const flow = toNumber(hp.water_flow_rate, 0);
      const mainP = toNumber(hp.main_pump_pressure, 0);
      const secP = toNumber(hp.secondary_pump_pressure, 0);
      const transP = toNumber(hp.transmission_pump_pressure, 0);
      const flowInt = Math.round(flow);
      const mainPInt = Math.round(mainP);
      const secPInt = Math.round(secP);
      const transPInt = Math.round(transP);
      const newHydraulicParams: HydraulicParam[] = [
        { value: flowInt, label: 'Расход воды (л/мин)', baseColor: '#2a3f3c', fillColor: '#00aaff', fillPercent: Math.round(clamp01(flow/500)*100), textColor: '#fff' },
        { value: mainPInt, label: 'Давление главного насоса (Бар)', baseColor: '#2a3f3c', fillColor: '#00ff6a', fillPercent: Math.round(clamp01(mainP/200)*100), textColor: '#fff' },
        { value: secPInt, label: 'Давление вспомогательного насоса (Бар)', baseColor: '#2a3f3c', fillColor: '#6affb7', fillPercent: Math.round(clamp01(secP/150)*100), textColor: '#fff' },
        { value: transPInt, label: 'Давление трансмиссионного насоса (Бар)', baseColor: '#ffb347', fillColor: '#8B0000', fillPercent: Math.round(clamp01(transP/150)*100), fromTop: true, textColor: '#d9534f' },
      ];
      if (!deepEqualArray(hydraulicParams, newHydraulicParams)) setHydraulicParams(newHydraulicParams);

      // Trajectory: split by point_type and take the latest point per depth for each series across all timestamps
      const points = Array.isArray(data.trajectory_points) ? data.trajectory_points : [];
      const toTime = (s?: string) => {
        const t = s ? Date.parse(s) : NaN;
        return Number.isFinite(t) ? t : 0;
      };
      const planMapTime = new Map<number, { tvd: number; time: number }>();
      const factMapTime = new Map<number, { tvd: number; time: number }>();
      for (const p of points) {
        const md = toNumber(p.measurement_depth ?? p.depth ?? 0, 0);
        const tvdNeg = -toNumber(p.true_vertical_depth ?? p.vertical_displacement ?? 0, 0);
        const time = toTime(p.measured_at);
        const t = String(p.point_type || '').toLowerCase();
        const upsert = (map: Map<number, { tvd: number; time: number }>) => {
          const prev = map.get(md);
          if (!prev || time >= prev.time) map.set(md, { tvd: tvdNeg, time });
        };
        if (t === 'planned' || t === 'plan') {
          upsert(planMapTime);
        } else if (t === 'actual' || t === 'fact') {
          upsert(factMapTime);
        } else {
          // Unknown type or new format without point_type — include into both as last-seen point
          upsert(planMapTime);
          upsert(factMapTime);
        }
      }
      const planMap = new Map<number, number>();
      const factMap = new Map<number, number>();
      planMapTime.forEach((v, k) => planMap.set(k, v.tvd));
      factMapTime.forEach((v, k) => factMap.set(k, v.tvd));
      const mdSet = new Set<number>();
      planMap.forEach((_, key) => mdSet.add(key));
      factMap.forEach((_, key) => mdSet.add(key));
      const mdList = Array.from(mdSet).sort((a, b) => a - b);
      const newTraj: TrajectoryDataPoint[] = mdList.map(md => ({
        name: md,
        plan: planMap.has(md) ? planMap.get(md)! : null,
        fact: factMap.has(md) ? factMap.get(md)! : null,
      }));
      if (!deepEqualArray(trajData, newTraj)) setTrajData(newTraj);

      // Progress
      const newProgress = (data.drilling_progress || []).map(pr => ({
        label: pr.stage_name || 'Этап',
        percent: Math.round(toNumber(pr.progress_percent, 0)),
      }));
      if (!deepEqualArray(progress, newProgress)) setProgress(newProgress);
    } catch (e) {
      console.error(e);
    }
  };

  // Initial load / on token
  useEffect(() => {
    if (!token) return;
    fetchStatic(selectedStationId);
    fetchDynamic(selectedStationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Refetch when rig changes
  useEffect(() => {
    if (!token) return;
    fetchStatic(selectedStationId);
    fetchDynamic(selectedStationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStationId]);

  // Polling dynamic every 10s
  useEffect(() => {
    if (!token) return;
    const id = window.setInterval(() => {
      fetchDynamic(selectedStationId);
    }, 10000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedStationId]);

  return (
    <div style={styles.dashboard}>
      <div style={styles.mainGrid}>
        <div style={styles.leftColumn}>
          <MemoDrillingParameters
            gaugeWater={gaugeWater}
            gaugePressure={gaugePressure}
            waterPressureValue={waterPressureValue}
            axialPressureValue={axialPressureValue}
            waterTempPercent={waterTempPercent}
            hydroTempPercent={hydroTempPercent}
            waterTempC={waterTempC}
            hydroTempC={hydroTempC}
          />
          <DrillingPlanMemo data={planData} delta={planDelta} />
        </div>
        <div style={styles.rightColumn}>
          <TopInfoBarMemo
            items={topInfo}
            rigs={rigs}
            selectedStationId={selectedStationId}
            onStationChange={setSelectedStationId}
          />
          <div style={styles.rightColumnGrid}>
            <div style={{...styles.widget, minHeight: 260, width: 300 }}>
              <CurrentProcessMemo operation={currentOp.operation} bitDepth={currentOp.bitDepth} wellDepth={currentOp.wellDepth} stationName={headerInfo.name} stationStatus={headerInfo.status} />
            </div>
            <div style={{...styles.widget, gridColumn: '2 / 4', minHeight: 260 }}>
              <HydraulicParametersMemo params={hydraulicParams} />
            </div>
            <div style={{gridColumn: '1 / -1'}}><SimpleTimelineMemo /></div>
            <div style={{gridColumn: '1 / -1'}}>
              <TrajectoryChartMemo data={trajData} />
            </div>
          </div>
        </div>
      </div>
      <ProgressBarMemo steps={progress} />
    </div>
  );
}

export default HomePage;

// Memoized components to reduce re-render cost when data unchanged
const MemoDrillingParameters = memo(DrillingParameters, (prev, next) => {
  return prev.waterPressureValue === next.waterPressureValue &&
    prev.axialPressureValue === next.axialPressureValue &&
    prev.waterTempPercent === next.waterTempPercent &&
    prev.hydroTempPercent === next.hydroTempPercent &&
    prev.waterTempC === next.waterTempC &&
    prev.hydroTempC === next.hydroTempC &&
    deepEqualArray(prev.gaugeWater, next.gaugeWater) &&
    deepEqualArray(prev.gaugePressure, next.gaugePressure);
});
const CurrentProcessMemo = memo(CurrentProcess, (a,b)=> JSON.stringify(a)===JSON.stringify(b));
const HydraulicParametersMemo = memo(HydraulicParameters, (a,b)=> deepEqualArray(a.params, b.params));
const DrillingPlanMemo = memo(DrillingPlan, (a,b)=> deepEqualArray(a.data, b.data) && a.delta === b.delta);
const TrajectoryChartMemo = memo(TrajectoryChart, (a,b)=> deepEqualArray(a.data, b.data));
const ProgressBarMemo = memo(ProgressBar, (a,b)=> deepEqualArray(a.steps, b.steps));
const TopInfoBarMemo = memo(TopInfoBar, (a,b)=> deepEqualArray(a.items, b.items) && deepEqualArray(a.rigs, b.rigs) && a.selectedStationId === b.selectedStationId);
const SimpleTimelineMemo = memo(SimpleTimeline);

// --- Styles ---

const styles: { [key: string]: CSSProperties } = {
  container: {
    display: "flex",
    background: "#18191c",
    color: "#fff",
  },
  mainContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    marginLeft: 80, 
    height: '100vh', 
    overflowY: 'auto', 
  },
  dashboard: {
    padding: 16,
    display: "flex",
    flexDirection: 'column',
    flex: 1,
    gap: 12,
  },
  mainGrid: {
      display: 'flex',
      gap: 12,
      flex: 1,
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 600px',
    gap: 12,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: 12,
  },
  rightColumnGrid: {
      display: 'grid',
      gridTemplateColumns: 'max-content 1fr 1fr',
      gap: 12,
      flex: 1,
  },
  widget: {
    background: "#1F2534",
    borderRadius: 16,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    border: '1px solid #2E3749',
    boxShadow: '0 2px 16px #0000003D',
  },
  widgetTitle: {
    marginBottom: 16,
    textAlign: "center",
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: 0.2,
  },
    topInfoBar: {
      display: 'flex',
      gap: 24,
      padding: '12px 18px',
      background: '#1F2534',
      borderRadius: 16,
      alignItems: 'center',
      border: '1px solid #2E3749',
      boxShadow: '0 2px 16px #0000003D',
      boxSizing: 'border-box',
      overflow: 'hidden'
    },
  
};